import { CanvasHtml } from "@dinahmoe/dmafjs-visual";

class LineTestWebGLEnvironment extends CanvasHtml {
  handlers = this.extendParentHandlers({});

  setProps(node) {
    super.setProps(node);

    const canvas = node.element;
    canvas.style.transform = "translateZ(0)";
    const canvasWidth = node.props.canvasWidth || 960;
    const aspectRatio = (canvas.clientHeight || 1080) / (canvas.clientWidth || 1920);
    canvas.width = canvasWidth;
    canvas.height = Math.round(canvasWidth * aspectRatio);

    const gl = canvas.getContext("webgl2", {
      antialias: false,
      preserveDrawingBuffer: true,
      alpha: false
    });
    if (!gl) {
      console.error("WebGL 2.0 not supported");
      return;
    }

    // Fixed config — same defaults as the real gradient
    const rootHeight = 1080;
    const config = {
      waveStrength: 0.158,
      blurEffectStrength: 0.5,
      ditherStrength: 20.0,
      noiseOctaves: 3,
      noiseInfluence: 0.659,
      animationSpeed: 0.2,
      peakAmplitude: 0.692,
      peakSpeed: 0.1,
      grainEnabled: false,
      grainOpacity: 0.0686,
      grainPixelSize: 1,
      grainDensity: 0.3,
      rootHeight,
      dotSpacing: 72,
      dotSize: 2,
      dotOpacity: 0.3,
    };

    // Build color map — same logic as the real gradient with fixed transitions
    const t = rootHeight * 0.1;
    const blue = "7429CC";
    const pink = "E88EA6";
    const red = "CC2237";
    const magenta = "CB298D";
    const p1 = rootHeight * 0.85;
    const p2 = rootHeight * 2;
    const p3 = rootHeight * 3;
    const p4 = rootHeight * 4;

    const colorMap = [
      { color: blue, position: -rootHeight / 3 },
      { color: blue, position: p1 - 1.5 * t },
      { color: pink, position: p1 - 0.5 * t },
      { color: red,  position: p1 + 0.5 * t },
      { color: magenta, position: p1 + 1.5 * t },
      { color: magenta, position: p2 - 1.5 * t },
      { color: red,     position: p2 - 0.5 * t },
      { color: pink,    position: p2 + 0.5 * t },
      { color: blue, position: p2 + 1.5 * t },
      { color: blue, position: p3 - 1.5 * t },
      { color: pink, position: p3 - 0.5 * t },
      { color: red,  position: p3 + 0.5 * t },
      { color: magenta, position: p3 + 1.5 * t },
      { color: magenta, position: p4 - 1.5 * t },
      { color: red,     position: p4 - 0.5 * t },
      { color: pink,    position: p4 + 0.5 * t },
      { color: blue, position: p4 + 1.5 * t },
      { color: blue, position: p4 + 1.5 * t + rootHeight },
    ];

    // Sweep range — from start of gradient to end
    const scrollMax = p4 + 1.5 * t + rootHeight;

    const vertexShaderSource = `#version 300 es
      in vec2 a_position;
      void main() {
        gl_Position = vec4(a_position, 0.0, 1.0);
      }
    `;

    // Exact same fragment shader as the gradient
    const fragmentShaderSource = `#version 300 es
      precision highp float;

      out vec4 fragColor;

      uniform vec2 u_resolution;
      uniform float u_time;
      uniform float u_frameCount;
      uniform vec3 u_colorStops[50];
      uniform float u_colorPositions[50];
      uniform float u_transitionCenters[50];
      uniform float u_zoneIndices[50];
      uniform int u_colorStopCount;
      uniform float u_waveStrength;
      uniform float u_blurEffect;
      uniform float u_ditherStrength;
      uniform int u_noiseOctaves;
      uniform float u_noiseInfluence;
      uniform float u_scrollOffset;
      uniform bool u_grainEnabled;
      uniform float u_grainOpacity;
      uniform float u_grainDensity;
      uniform float u_grainPixelSize;
      uniform float u_rawTime;
      uniform float u_rootHeight;
      uniform float u_peakAmplitude;
      uniform float u_peakSpeed;
      uniform float u_dotScrollOffset;
      uniform float u_dotSpacing;
      uniform float u_dotSize;
      uniform float u_dotOpacity;

      uint hash(uint x) {
        x += (x << 10u);
        x ^= (x >> 6u);
        x += (x << 3u);
        x ^= (x >> 11u);
        x += (x << 15u);
        return x;
      }

      uint hash(uvec2 v) {
        return hash(v.x ^ hash(v.y));
      }

      float floatConstruct(uint m) {
        const uint ieeeMantissa = 0x007FFFFFu;
        const uint ieeeOne = 0x3F800000u;
        m &= ieeeMantissa;
        m |= ieeeOne;
        float f = uintBitsToFloat(m);
        return f - 1.0;
      }

      float random(vec2 v) {
        return floatConstruct(hash(floatBitsToUint(v)));
      }

      float random3(vec3 v) {
        uvec3 uv = floatBitsToUint(v);
        return floatConstruct(hash(uv.x ^ hash(uv.y) ^ hash(uv.z)));
      }

      float fractalNoise(float x, float t, float baseFreq, float timeSpeed) {
        float val = 0.0;
        float frequency = baseFreq;
        float amplitude = 1.0;
        float persistence = 0.5;

        for (int i = 0; i < 10; i++) {
          if (i >= u_noiseOctaves) break;
          val += sin((x * frequency) + (t * timeSpeed)) * amplitude;
          frequency *= 2.0;
          amplitude *= persistence;
        }
        return val;
      }

      void main() {
        vec2 uv = gl_FragCoord.xy;
        float x = uv.x;
        float y = u_resolution.y - uv.y;

        float baseSineWave = sin((x * 0.002) - u_time);
        float detailedNoise = fractalNoise(x, u_time, 0.0025, -0.0008);
        float baseWave = (baseSineWave * (u_waveStrength * (1.0 - u_noiseInfluence))) +
                         (detailedNoise * (u_waveStrength * u_noiseInfluence));

        float effectiveY = y - baseWave;

        float cssY = effectiveY * (u_rootHeight / u_resolution.y);

        float basePeakPhase = (x / u_resolution.x) * 3.14159 + u_rawTime * u_peakSpeed;

        float adjustedPositions[50];
        for (int i = 0; i < 50; i++) {
          if (i >= u_colorStopCount) break;
          float zonePhase = basePeakPhase + u_zoneIndices[i] * 2.094;
          float peak = sin(zonePhase);
          float scale = 1.0 + peak * u_peakAmplitude;
          adjustedPositions[i] = u_transitionCenters[i] + (u_colorPositions[i] - u_transitionCenters[i]) * scale;
        }

        float dither = random(uv) - 0.5;
        float gradientY = cssY - u_scrollOffset + (dither * u_ditherStrength);

        vec3 color;

        if (gradientY <= adjustedPositions[0]) {
          color = u_colorStops[0];
        }
        else if (gradientY >= adjustedPositions[u_colorStopCount - 1]) {
          color = u_colorStops[u_colorStopCount - 1];
        }
        else {
          for (int i = 0; i < 49; i++) {
            if (i >= u_colorStopCount - 1) break;

            if (gradientY >= adjustedPositions[i] && gradientY < adjustedPositions[i + 1]) {
              float segmentStart = adjustedPositions[i];
              float segmentEnd = adjustedPositions[i + 1];
              float segmentLength = segmentEnd - segmentStart;

              float progress = (gradientY - segmentStart) / max(segmentLength, 1.0);

              progress = progress - (u_blurEffect * sin(progress * 3.14159) * (1.0 - progress));
              progress = clamp(progress, 0.0, 1.0);

              color = mix(u_colorStops[i], u_colorStops[i + 1], progress);
              break;
            }
          }
        }

        if (u_grainEnabled) {
          vec2 grainUv = floor(uv / u_grainPixelSize) * u_grainPixelSize;
          vec3 grainInput = vec3(grainUv, u_frameCount);
          float grain = random3(grainInput);
          if (grain < u_grainDensity) {
            color = mix(color, vec3(1.0), u_grainOpacity);
          }
        }

        if (u_dotOpacity > 0.0 && u_dotSpacing > 0.0) {
          float dotStart = adjustedPositions[4];
          float dotEnd = adjustedPositions[5];
          float dotFactor = smoothstep(dotStart - 20.0, dotStart + 20.0, gradientY)
                          * (1.0 - smoothstep(dotEnd - 20.0, dotEnd + 20.0, gradientY));

          if (dotFactor > 0.0) {
            float dotScale = u_resolution.y / u_rootHeight;
            float spacing = u_dotSpacing * dotScale;
            float radius = u_dotSize * dotScale;

            float dotY = y + u_dotScrollOffset * dotScale;
            float dotX = x;

            float dotXFromCenter = dotX - u_resolution.x * 0.5;

            float cellX = mod(dotXFromCenter, spacing) - spacing * 0.5;
            float cellY = mod(dotY, spacing) - spacing * 0.5;
            float dist = length(vec2(cellX, cellY));

            float dotAlpha = 1.0 - smoothstep(radius - 0.5, radius + 0.5, dist);

            color = mix(color, vec3(1.0), dotFactor * dotAlpha * u_dotOpacity);
          }
        }

        fragColor = vec4(color, 1.0);
      }
    `;

    function compileShader(source, type) {
      const shader = gl.createShader(type);
      gl.shaderSource(shader, source);
      gl.compileShader(shader);
      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        console.error("Shader compile error:", gl.getShaderInfoLog(shader));
        gl.deleteShader(shader);
        return null;
      }
      return shader;
    }

    const vertexShader = compileShader(vertexShaderSource, gl.VERTEX_SHADER);
    const fragmentShader = compileShader(fragmentShaderSource, gl.FRAGMENT_SHADER);

    const program = gl.createProgram();
    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      console.error("Program link error:", gl.getProgramInfoLog(program));
      return;
    }

    gl.useProgram(program);

    const vao = gl.createVertexArray();
    gl.bindVertexArray(vao);

    const positionBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);

    const positionLocation = gl.getAttribLocation(program, "a_position");
    gl.enableVertexAttribArray(positionLocation);
    gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0);

    const uniforms = {
      resolution: gl.getUniformLocation(program, "u_resolution"),
      time: gl.getUniformLocation(program, "u_time"),
      frameCount: gl.getUniformLocation(program, "u_frameCount"),
      colorStops: gl.getUniformLocation(program, "u_colorStops"),
      colorPositions: gl.getUniformLocation(program, "u_colorPositions"),
      transitionCenters: gl.getUniformLocation(program, "u_transitionCenters"),
      zoneIndices: gl.getUniformLocation(program, "u_zoneIndices"),
      colorStopCount: gl.getUniformLocation(program, "u_colorStopCount"),
      waveStrength: gl.getUniformLocation(program, "u_waveStrength"),
      blurEffect: gl.getUniformLocation(program, "u_blurEffect"),
      ditherStrength: gl.getUniformLocation(program, "u_ditherStrength"),
      noiseOctaves: gl.getUniformLocation(program, "u_noiseOctaves"),
      noiseInfluence: gl.getUniformLocation(program, "u_noiseInfluence"),
      scrollOffset: gl.getUniformLocation(program, "u_scrollOffset"),
      grainEnabled: gl.getUniformLocation(program, "u_grainEnabled"),
      grainOpacity: gl.getUniformLocation(program, "u_grainOpacity"),
      grainDensity: gl.getUniformLocation(program, "u_grainDensity"),
      grainPixelSize: gl.getUniformLocation(program, "u_grainPixelSize"),
      rawTime: gl.getUniformLocation(program, "u_rawTime"),
      rootHeight: gl.getUniformLocation(program, "u_rootHeight"),
      peakAmplitude: gl.getUniformLocation(program, "u_peakAmplitude"),
      peakSpeed: gl.getUniformLocation(program, "u_peakSpeed"),
      dotScrollOffset: gl.getUniformLocation(program, "u_dotScrollOffset"),
      dotSpacing: gl.getUniformLocation(program, "u_dotSpacing"),
      dotSize: gl.getUniformLocation(program, "u_dotSize"),
      dotOpacity: gl.getUniformLocation(program, "u_dotOpacity"),
    };

    function hexToRgb(hex) {
      const r = parseInt(hex.substring(0, 2), 16) / 255;
      const g = parseInt(hex.substring(2, 4), 16) / 255;
      const b = parseInt(hex.substring(4, 6), 16) / 255;
      return [r, g, b];
    }

    function processColorMap(cm) {
      const colors = [];
      const positions = [];

      for (const stop of cm) {
        colors.push(...hexToRgb(stop.color));
        positions.push(stop.position);
      }

      const transitionCenters = new Array(cm.length);
      const isTransition = (idx) => {
        if (idx <= 0 || idx >= cm.length - 1) return false;
        return cm[idx].color !== cm[idx - 1].color &&
               cm[idx].color !== cm[idx + 1].color;
      };

      const zoneIndices = new Array(cm.length);
      for (let i = 0; i < cm.length; i++) {
        transitionCenters[i] = positions[i];
        zoneIndices[i] = 0;
      }

      let i = 0;
      let zoneCount = 0;
      while (i < cm.length) {
        if (isTransition(i)) {
          const start = Math.max(0, i - 1);
          let end = i;
          while (end < cm.length - 1 && isTransition(end + 1)) end++;
          end = Math.min(cm.length - 1, end + 1);

          const center = (positions[start] + positions[end]) / 2;
          for (let j = start; j <= end; j++) {
            transitionCenters[j] = center;
            zoneIndices[j] = zoneCount + 1;
          }
          zoneCount++;
          i = end + 1;
        } else {
          i++;
        }
      }

      return { colors, positions, transitionCenters, zoneIndices, count: cm.length };
    }

    // Process color map once — it never changes
    const { colors, positions, transitionCenters, zoneIndices, count } = processColorMap(colorMap);

    // Offscreen FBO — render here first, then blit to screen atomically
    const fbo = gl.createFramebuffer();
    const fboTexture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, fboTexture);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, canvas.width, canvas.height, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, fboTexture, 0);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);

    node._isPaused = false;

    // Sweep speed — scroll offset driven from JS at constant speed
    const CYCLE_DURATION = 3124;

    let frameCount = 0;
    let lastFrameTime = 0;
    let droppedFrames = 0;
    function render(currentTime) {
      if (node._isPaused) {
        node._animationFrameId = requestAnimationFrame(render);
        return;
      }

      const delta = currentTime - lastFrameTime;
      if (lastFrameTime > 0 && delta > 30) {
        droppedFrames++;
        const missed = Math.round(delta / 16.667) - 1;
        console.warn(`[line-test] dropped ${missed} frame(s): ${delta.toFixed(1)}ms gap (total dropped: ${droppedFrames})`);
      }
      lastFrameTime = currentTime;

      frameCount++;

      const targetWidth = node.props.canvasWidth || 960;
      const aspectRatio = (canvas.clientHeight || 1080) / (canvas.clientWidth || 1920);
      const targetHeight = Math.round(targetWidth * aspectRatio);
      if (canvas.width !== targetWidth || canvas.height !== targetHeight) {
        canvas.width = targetWidth;
        canvas.height = targetHeight;
        // Resize FBO texture to match
        gl.bindTexture(gl.TEXTURE_2D, fboTexture);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, canvas.width, canvas.height, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
      }

      // Render to offscreen FBO
      gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.clear(gl.COLOR_BUFFER_BIT);

      // Scroll offset computed in JavaScript — sweeps through full gradient
      const sweepProgress = (currentTime % CYCLE_DURATION) / CYCLE_DURATION;
      const scrollOffset = -sweepProgress * scrollMax;
      const dotScrollOffset = sweepProgress * scrollMax * 0.75;

      gl.uniform2f(uniforms.resolution, canvas.width, canvas.height);
      gl.uniform1f(uniforms.time, currentTime * config.animationSpeed / 1000);
      gl.uniform1f(uniforms.frameCount, frameCount);
      gl.uniform3fv(uniforms.colorStops, colors);
      gl.uniform1fv(uniforms.colorPositions, positions);
      gl.uniform1fv(uniforms.transitionCenters, transitionCenters);
      gl.uniform1fv(uniforms.zoneIndices, zoneIndices);
      gl.uniform1i(uniforms.colorStopCount, count);
      gl.uniform1f(uniforms.waveStrength, config.waveStrength * 400);
      gl.uniform1f(uniforms.blurEffect, config.blurEffectStrength);
      gl.uniform1f(uniforms.ditherStrength, config.ditherStrength);
      gl.uniform1i(uniforms.noiseOctaves, config.noiseOctaves);
      gl.uniform1f(uniforms.noiseInfluence, config.noiseInfluence);
      gl.uniform1f(uniforms.scrollOffset, scrollOffset);
      gl.uniform1i(uniforms.grainEnabled, config.grainEnabled ? 1 : 0);
      gl.uniform1f(uniforms.grainOpacity, config.grainOpacity);
      gl.uniform1f(uniforms.grainDensity, config.grainDensity);
      gl.uniform1f(uniforms.grainPixelSize, config.grainPixelSize);
      gl.uniform1f(uniforms.rawTime, currentTime);
      gl.uniform1f(uniforms.rootHeight, config.rootHeight);
      gl.uniform1f(uniforms.peakAmplitude, config.peakAmplitude);
      gl.uniform1f(uniforms.peakSpeed, config.peakSpeed / 1000);
      gl.uniform1f(uniforms.dotScrollOffset, dotScrollOffset);
      gl.uniform1f(uniforms.dotSpacing, config.dotSpacing);
      gl.uniform1f(uniforms.dotSize, config.dotSize);
      gl.uniform1f(uniforms.dotOpacity, config.dotOpacity);

      gl.bindVertexArray(vao);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);

      // Blit FBO to screen in one atomic operation
      gl.bindFramebuffer(gl.READ_FRAMEBUFFER, fbo);
      gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, null);
      gl.blitFramebuffer(0, 0, canvas.width, canvas.height, 0, 0, canvas.width, canvas.height, gl.COLOR_BUFFER_BIT, gl.NEAREST);
      gl.flush();

      node._animationFrameId = requestAnimationFrame(render);
    }

    requestAnimationFrame(render);
  }

  destroy(node) {
    if (node._animationFrameId) {
      cancelAnimationFrame(node._animationFrameId);
      node._animationFrameId = null;
    }
    super.destroy(node);
  }
}

const lineTestWebGLEnvironment = new LineTestWebGLEnvironment("visual:line-test-webgl");
export { LineTestWebGLEnvironment, lineTestWebGLEnvironment };
